import { faChevronDown, faChevronUp, faCircle, faRefresh, faTasks } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { EXPLAIN_CURRENT_VISIT, SCAN_MULTI } from "../../../../amplify/api-gateway/write";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { isSectionUnread, processTextForCopy } from "../utils";
import CopyToClipboardButton from "../../../assistant/CopyToClipboardButton";
import { dashboardLLMCache } from "../cache";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../../../utils/time";
import { ModuleErrorDisplay } from "../common/ModuleErrorDisplay";


interface SectionCurrentVisitProps {
  className?: string;
  user: any;
  isLoadingCurrentVisitPresentation: boolean;
  currentVisitPresentation: any;
  patientVisit: any;
  feedbacks: any[];
  setFeedbacks: any;
  statuses: any;
  reasonsForVisit?: string;
  setRegenerateTarget: any;
  setShowContextMenu: any;
  handleShowScanMultiModal: any;
  unreadFileVersionMap?: Record<string, any>;
  latestFileVersions?: any;
  sectionToSetter?: any;
  handleFileVersionChange?: any;
  tenant: any;
}

export const SectionCurrentVisit = ({
  className = '',
  user,
  isLoadingCurrentVisitPresentation,
  currentVisitPresentation,
  patientVisit,
  feedbacks,
  setFeedbacks,
  statuses,
  reasonsForVisit,
  setRegenerateTarget,
  setShowContextMenu,
  handleShowScanMultiModal,
  unreadFileVersionMap,
  latestFileVersions,
  sectionToSetter,
  handleFileVersionChange,
  tenant,
}: SectionCurrentVisitProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_CURRENT_VISIT]) {
            const version = latestFileVersions[EXPLAIN_CURRENT_VISIT];

            // console.log("Running GET_FILE_AT_VERSION with version ", latestFileVersions);
            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                setScanMulti(data);
            })
        }
    }, [latestFileVersions]);

    const getTextToCopy = () => {
        let anchorDate = new Date();
        if (latestFileVersions && latestFileVersions[EXPLAIN_CURRENT_VISIT]) {
            anchorDate = new Date(latestFileVersions[EXPLAIN_CURRENT_VISIT].version_date);
        }

        return `Current Visit Summary as of ${getLocalDateStrFromDate(anchorDate)} ${getLocalTimeStrFromDate(anchorDate)}. ` + processTextForCopy(currentVisitPresentation.current_visit_presentation + "\n", anchorDate);
    };

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingCurrentVisitPresentation} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faTasks} fixedWidth /></span><strong>Current Visit Summary</strong> {isSectionUnread(EXPLAIN_CURRENT_VISIT, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={8} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_CURRENT_VISIT} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />

                        <CopyToClipboardButton user={user} module={"current_visit"} onClick={() => {navigator.clipboard.writeText(getTextToCopy())}} customStyling="dashboard-round-btn" overlayTriggerMessage="Copy Patient Summary" />

                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_CURRENT_VISIT); setShowContextMenu(true);}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"current_visit"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        {currentVisitPresentation && currentVisitPresentation["error"] ? (
                            <Row className="mt-2">
                                <Col md={12}>
                                    <ModuleErrorDisplay
                                        errorMessage={currentVisitPresentation["error"]}
                                        moduleTitle="Current Visit Summary"
                                        hasPreviousVersions={!!(latestFileVersions && latestFileVersions[EXPLAIN_CURRENT_VISIT])}
                                    />
                                </Col>
                            </Row>
                        ) : currentVisitPresentation ? (
                            <Row className="mt-2">
                                <Col md={12} className="description-text">
                                    <LLMDashboardMarkdownRenderer content={currentVisitPresentation.current_visit_presentation} scanMulti={scanMulti} />
                                </Col>
                            </Row>
                        ) : (
                            <Row className="mt-2">
                                <Col md={12}>
                                    <>No Data</>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
                {
                    isExpanded && reasonsForVisit && (
                        <Row>
                            <Col className="text-muted" style={{fontSize: '0.875rem'}}>
                                <i>Reasons for Visit: {reasonsForVisit} {typeof reasonsForVisit}</i>
                            </Col>
                        </Row>
                    )
                }
            </Card.Body>
        </Card>
    );
};
